<script setup>
// NOTICE: KAPTION-INTEGRATION
// Modal for selecting Kaption utility templates
// Shows templates organized by variant with preview
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import KaptionCircleFlag from 'dashboard/components-next/flag/KaptionCircleFlag.vue';

const props = defineProps({
  /**
   * Whether the modal is visible
   */
  show: {
    type: Boolean,
    default: false,
  },
  /**
   * All available templates organized by type and language
   */
  templates: {
    type: Object,
    required: true,
  },
  /**
   * Template type: 'followup', 'scheduled', 'reminder'
   */
  templateType: {
    type: String,
    required: true,
  },
  /**
   * Currently selected template
   */
  selectedTemplate: {
    type: Object,
    default: null,
  },
  /**
   * Current language
   */
  language: {
    type: String,
    default: 'en',
  },
});

const emit = defineEmits(['close', 'select', 'changeLanguage', 'changeType']);

const { t } = useI18n();

// Local state
const searchQuery = ref('');
const selectedLang = ref(props.language);
const selectedType = ref(props.templateType);

// Template types configuration
const TEMPLATE_TYPES = [
  { key: 'followup', label: 'Follow-up', icon: 'i-lucide-message-circle' },
  { key: 'scheduled', label: 'Scheduled', icon: 'i-lucide-calendar-clock' },
  { key: 'reminder', label: 'Reminder', icon: 'i-lucide-bell' },
];

// Sync language
watch(
  () => props.language,
  val => {
    selectedLang.value = val;
  }
);

// Sync type
watch(
  () => props.templateType,
  val => {
    selectedType.value = val;
  }
);

// NOTICE: KAPTION-INTEGRATION - Get template count for a type IN THE SELECTED LANGUAGE
const getTypeCountForLanguage = type => {
  const typeTemplates = props.templates[type];
  if (!typeTemplates) return 0;
  return typeTemplates[selectedLang.value]?.length || 0;
};

// Check if type has any templates in selected language
const typeHasTemplatesInLanguage = type => getTypeCountForLanguage(type) > 0;

// Handle type change
const handleTypeChange = type => {
  selectedType.value = type;
  emit('changeType', type);
};

// NOTICE: KAPTION-INTEGRATION - Check if language has ANY templates across all types
const languageHasAnyTemplates = lang => {
  return ['followup', 'scheduled', 'reminder'].some(type => {
    const typeTemplates = props.templates[type];
    return typeTemplates?.[lang]?.length > 0;
  });
};

// Supported languages
const SUPPORTED_LANGUAGES = ['en', 'es', 'pt_BR', 'fr', 'de', 'it'];

const LANG_NAMES = {
  en: 'English',
  es: 'Español',
  pt_BR: 'Português',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
};

// NOTICE: KAPTION-INTEGRATION - Show all supported languages, mark which have templates
const availableLanguages = computed(() => {
  // Return all supported languages so user can see availability
  return SUPPORTED_LANGUAGES;
});

// NOTICE: KAPTION-INTEGRATION - Get templates for selected type and language
// NO fallback to other languages - only show templates in selected language
const templatesForLanguage = computed(() => {
  const typeTemplates = props.templates[selectedType.value];
  if (!typeTemplates) return [];
  return typeTemplates[selectedLang.value] || [];
});

// Filter templates by search
const filteredTemplates = computed(() => {
  if (!searchQuery.value.trim()) return templatesForLanguage.value;

  const query = searchQuery.value.toLowerCase();
  return templatesForLanguage.value.filter(
    tpl =>
      tpl.body?.toLowerCase().includes(query) ||
      tpl.variant?.toLowerCase().includes(query)
  );
});

// Normalize language code for flags
const normalizeLanguageCode = lang => {
  return lang?.replace('_BR', '') || 'en';
};

// Handle language change
const handleLanguageChange = lang => {
  selectedLang.value = lang;
  emit('changeLanguage', lang);
};

// Handle template selection
const handleSelect = template => {
  emit('select', template);
  emit('close');
};

// Close modal
const handleClose = () => {
  searchQuery.value = '';
  emit('close');
};

// Format template body for preview (truncate and clean)
const formatPreview = body => {
  if (!body) return '';
  // Replace {{1}} with placeholder indicator
  let preview = body.replace(/\{\{1\}\}/g, '[...]');
  // Replace markdown bold
  preview = preview.replace(/\*([^*]+)\*/g, '$1');
  // Truncate if too long
  if (preview.length > 120) {
    preview = preview.substring(0, 120) + '...';
  }
  return preview;
};

// Get variant label (R1, R2, etc.)
const getVariantLabel = variant => {
  return variant?.toUpperCase() || '';
};
</script>

<template>
  <Modal
    :show="show"
    :on-close="handleClose"
    size="medium"
    class="template-selector-modal"
    @close="handleClose"
  >
    <template #header>
      <div class="modal-header">
        <h2 class="modal-title">
          {{
            t('KAPTION_UTILITY_TEMPLATES.SELECT_TEMPLATE', 'Select Template')
          }}
        </h2>
        <p class="modal-subtitle">
          {{
            t(
              'KAPTION_UTILITY_TEMPLATES.CHOOSE_TEMPLATE_DESCRIPTION',
              'Choose a template to send as a follow-up message'
            )
          }}
        </p>
      </div>
    </template>

    <template #default>
      <div class="modal-content">
        <!-- NOTICE: KAPTION-INTEGRATION - Type Tabs (followup/scheduled/reminder) - counts based on selected language -->
        <div class="type-tabs">
          <button
            v-for="typeConfig in TEMPLATE_TYPES"
            :key="typeConfig.key"
            class="type-tab"
            :class="{
              'is-active': selectedType === typeConfig.key,
              'is-empty': !typeHasTemplatesInLanguage(typeConfig.key),
            }"
            @click="handleTypeChange(typeConfig.key)"
          >
            <Icon :icon="typeConfig.icon" class="size-4" />
            <span class="type-label">{{ typeConfig.label }}</span>
            <span class="type-count">{{
              getTypeCountForLanguage(typeConfig.key)
            }}</span>
          </button>
        </div>

        <!-- Language & Search Bar -->
        <div class="filters-bar">
          <!-- Language Tabs - Show all languages with availability indicators -->
          <div class="language-tabs">
            <button
              v-for="lang in availableLanguages"
              :key="lang"
              class="lang-tab"
              :class="{
                'is-active': lang === selectedLang,
                'is-unavailable': !languageHasAnyTemplates(lang),
              }"
              @click="handleLanguageChange(lang)"
            >
              <KaptionCircleFlag
                :language-code="normalizeLanguageCode(lang)"
                size="size-4"
              />
              <span class="lang-label">{{ LANG_NAMES[lang] }}</span>
              <Icon
                v-if="!languageHasAnyTemplates(lang)"
                icon="i-lucide-alert-circle"
                class="size-3 text-n-ruby-9"
              />
            </button>
          </div>

          <!-- Search -->
          <div class="search-box">
            <Icon icon="i-lucide-search" class="size-4 text-n-slate-9" />
            <input
              v-model="searchQuery"
              type="text"
              class="search-input"
              :placeholder="
                t(
                  'KAPTION_UTILITY_TEMPLATES.SEARCH_PLACEHOLDER',
                  'Search templates...'
                )
              "
            />
            <button
              v-if="searchQuery"
              class="clear-btn"
              @click="searchQuery = ''"
            >
              <Icon icon="i-lucide-x" class="size-3" />
            </button>
          </div>
        </div>

        <!-- Templates Grid -->
        <div class="templates-grid">
          <button
            v-for="tpl in filteredTemplates"
            :key="tpl.name"
            class="template-card"
            :class="{ 'is-selected': selectedTemplate?.name === tpl.name }"
            @click="handleSelect(tpl)"
          >
            <div class="card-header">
              <span class="variant-badge">{{
                getVariantLabel(tpl.variant)
              }}</span>
              <Icon
                v-if="selectedTemplate?.name === tpl.name"
                icon="i-lucide-check-circle"
                class="size-4 text-n-green-11"
              />
            </div>
            <p class="template-preview">{{ formatPreview(tpl.body) }}</p>
          </button>

          <!-- Empty State - Different messages for search vs no templates -->
          <div v-if="filteredTemplates.length === 0" class="empty-state">
            <template v-if="searchQuery.trim()">
              <Icon icon="i-lucide-search-x" class="size-8 text-n-slate-8" />
              <p>
                {{ t('KAPTION_UTILITY_TEMPLATES.NO_SEARCH_RESULTS') }}
              </p>
            </template>
            <template v-else-if="!languageHasAnyTemplates(selectedLang)">
              <Icon
                icon="i-lucide-alert-triangle"
                class="size-8 text-n-amber-9"
              />
              <p class="text-n-amber-11 font-medium">
                {{ t('KAPTION_UTILITY_TEMPLATES.NO_TEMPLATES_IN_LANGUAGE') }}
              </p>
              <p class="text-xs text-n-slate-10 mt-1">
                {{ t('KAPTION_UTILITY_TEMPLATES.SELECT_ANOTHER_LANGUAGE') }}
              </p>
            </template>
            <template v-else>
              <Icon icon="i-lucide-inbox" class="size-8 text-n-slate-8" />
              <p>
                {{
                  t(
                    'KAPTION_UTILITY_TEMPLATES.NO_TEMPLATES_FOR_TYPE',
                    'No {type} templates in {language}',
                    { type: selectedType, language: LANG_NAMES[selectedLang] }
                  )
                }}
              </p>
            </template>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="modal-footer">
        <NextButton slate faded @click="handleClose">
          {{ t('KAPTION_UTILITY_TEMPLATES.CANCEL', 'Cancel') }}
        </NextButton>
      </div>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.modal-header {
  @apply pb-2;
}

.modal-title {
  @apply text-lg font-semibold text-n-slate-12;
}

.modal-subtitle {
  @apply text-sm text-n-slate-11 mt-1;
}

.modal-content {
  @apply flex flex-col gap-4;
}

/* NOTICE: KAPTION-INTEGRATION - Type Tabs */
.type-tabs {
  @apply flex gap-2 p-1 rounded-xl bg-n-alpha-1;
}

.type-tab {
  @apply flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg;
  @apply text-sm font-medium text-n-slate-11;
  @apply transition-all duration-150;

  &:hover:not(.is-active) {
    @apply bg-n-alpha-2;
  }

  &.is-active {
    @apply bg-n-solid-1 text-n-slate-12 shadow-sm;
  }

  &.is-empty {
    @apply opacity-50;
  }

  &.is-empty.is-active {
    @apply opacity-100;
  }
}

.type-label {
  @apply hidden sm:inline;
}

.type-count {
  @apply text-xs px-1.5 py-0.5 rounded-full bg-n-alpha-2;
  @apply min-w-[1.5rem] text-center;

  .is-active & {
    @apply bg-n-green-3 text-n-green-11;
  }

  .is-empty & {
    @apply bg-n-ruby-3/50 text-n-ruby-11;
  }
}

/* Filters Bar */
.filters-bar {
  @apply flex flex-col gap-3;
}

.language-tabs {
  @apply flex flex-wrap gap-2;
}

.lang-tab {
  @apply flex items-center gap-1.5 px-3 py-1.5 rounded-lg;
  @apply text-sm text-n-slate-11 bg-n-alpha-1;
  @apply border border-transparent;
  @apply hover:bg-n-alpha-2 transition-colors;

  &.is-active {
    @apply bg-n-green-3/30 text-n-green-11 border-n-green-6;
  }

  &.is-unavailable {
    @apply opacity-50;

    &.is-active {
      @apply bg-n-ruby-3/20 text-n-ruby-11 border-n-ruby-6;
    }
  }
}

.lang-label {
  @apply text-xs font-medium;
}

.search-box {
  @apply flex items-center gap-2 px-3 py-2;
  @apply bg-n-alpha-1 rounded-lg border border-n-weak;

  &:focus-within {
    @apply border-n-slate-7;
  }
}

.search-input {
  @apply flex-1 bg-transparent text-sm text-n-slate-12 outline-none;

  &::placeholder {
    @apply text-n-slate-9;
  }
}

.clear-btn {
  @apply flex items-center justify-center size-5 rounded;
  @apply text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12;
}

/* Templates Grid */
.templates-grid {
  @apply grid grid-cols-1 gap-3 max-h-[400px] overflow-y-auto pr-1;
}

.template-card {
  @apply flex flex-col gap-2 p-4 rounded-xl text-left;
  @apply bg-n-solid-2 border border-n-weak;
  @apply hover:border-n-slate-7 hover:bg-n-alpha-1 transition-all;
  @apply cursor-pointer;

  &.is-selected {
    @apply border-n-green-7 bg-n-green-3/20;
  }
}

.card-header {
  @apply flex items-center justify-between;
}

.variant-badge {
  @apply text-xs font-bold text-n-slate-10 uppercase;
  @apply px-2 py-0.5 rounded bg-n-alpha-2;
}

.template-preview {
  @apply text-sm text-n-slate-12 leading-relaxed;
}

.empty-state {
  @apply flex flex-col items-center justify-center gap-2 py-8;
  @apply text-sm text-n-slate-10;
}

/* Modal Footer */
.modal-footer {
  @apply flex justify-end gap-2;
}
</style>
